import React, { useState } from 'react';
import {
  Users, Plus, UserPlus, Trash2, ChevronDown, ChevronUp,
  Clock, CheckCircle, XCircle, Edit3, Eye, MoreHorizontal, Pencil, Check, X
} from 'lucide-react';
import { useTeams } from '../../hooks/useTeams';
import { useAuth } from '../../hooks/useAuth';
import { CreateTeamModal } from './CreateTeamModal';
import { InviteMemberModal } from './InviteMemberModal';
import type { Team, TeamMember, TeamRole } from '../../types/teams';

const ROLE_LABELS: Record<TeamRole, string> = { viewer: 'Viewer', editor: 'Editor' };

const StatusBadge = ({ status }: { status: TeamMember['status'] }) => {
  if (status === 'accepted') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald/10 text-emerald">
      <CheckCircle className="h-3 w-3" /> Active
    </span>
  );
  if (status === 'pending') return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
      <Clock className="h-3 w-3" /> Pending
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
      <XCircle className="h-3 w-3" /> Declined
    </span>
  );
};

const RoleBadge = ({ role }: { role: TeamRole }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
    role === 'editor' ? 'bg-electric/10 text-electric' : 'bg-sand/30 text-slate'
  }`}>
    {role === 'editor' ? <Edit3 className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
    {ROLE_LABELS[role]}
  </span>
);

function TeamCard({
  team,
  isOwner,
  onDelete,
  onRename,
  onInvite,
  onRemoveMember,
  onUpdateRole,
}: {
  team: Team;
  isOwner: boolean;
  onDelete: () => void;
  onRename: (name: string) => Promise<void>;
  onInvite: () => void;
  onRemoveMember: (memberId: string) => void;
  onUpdateRole: (memberId: string, role: TeamRole) => void;
}) {
  const [expanded, setExpanded] = useState(true);
  const [editing, setEditing] = useState(false);
  const [nameValue, setNameValue] = useState(team.name);
  const [renamingSaving, setRenamingSaving] = useState(false);
  const members = team.members || [];

  const handleRename = async () => {
    if (!nameValue.trim() || nameValue === team.name) { setEditing(false); return; }
    setRenamingSaving(true);
    await onRename(nameValue.trim());
    setEditing(false);
    setRenamingSaving(false);
  };

  return (
    <div className="bg-white rounded-lg border border-sand/20 shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="bg-electric/10 p-2 rounded-lg">
            <Users className="h-5 w-5 text-electric" />
          </div>
          {editing ? (
            <div className="flex items-center gap-2 flex-1">
              <input
                value={nameValue}
                onChange={e => setNameValue(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleRename(); if (e.key === 'Escape') setEditing(false); }}
                className="flex-1 px-2 py-1 border border-electric rounded text-midnight font-semibold focus:outline-none"
                autoFocus
              />
              <button onClick={handleRename} disabled={renamingSaving} className="p-1 text-emerald hover:text-emerald/80">
                <Check className="h-4 w-4" />
              </button>
              <button onClick={() => setEditing(false)} className="p-1 text-slate hover:text-midnight">
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2 min-w-0">
              <span className="font-semibold text-midnight truncate">{team.name}</span>
              <span className="text-xs text-slate shrink-0">{members.length} member{members.length !== 1 ? 's' : ''}</span>
              {isOwner && (
                <button onClick={() => setEditing(true)} className="p-1 text-slate hover:text-midnight opacity-0 group-hover:opacity-100 transition-opacity">
                  <Pencil className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 ml-3">
          {isOwner && (
            <>
              <button
                onClick={onInvite}
                className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 text-sm bg-electric/10 text-electric rounded-lg hover:bg-electric/20 transition-colors"
              >
                <UserPlus className="h-3.5 w-3.5" />
                Invite
              </button>
              <button
                onClick={onDelete}
                className="p-2 text-slate hover:text-crimson rounded-lg transition-colors"
                title="Delete team"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </>
          )}
          <button onClick={() => setExpanded(v => !v)} className="p-1 text-slate hover:text-midnight">
            {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-sand/10">
          {members.length === 0 ? (
            <div className="px-5 py-6 text-center text-slate text-sm">
              No members yet.{isOwner && ' Invite someone to get started.'}
            </div>
          ) : (
            <div className="divide-y divide-sand/10">
              {members.map(member => (
                <div key={member.id} className="flex items-center justify-between px-5 py-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="bg-sand/20 rounded-full p-2">
                      <Users className="h-3.5 w-3.5 text-slate" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-midnight truncate">
                        {member.full_name || member.invited_email}
                      </div>
                      <div className="text-xs text-slate truncate">{member.invited_email}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-3 shrink-0">
                    <StatusBadge status={member.status} />
                    {isOwner && member.status === 'accepted' ? (
                      <select
                        value={member.role}
                        onChange={e => onUpdateRole(member.id, e.target.value as TeamRole)}
                        className="text-xs border border-sand/30 rounded px-1.5 py-0.5 text-midnight focus:ring-1 focus:ring-electric"
                      >
                        <option value="viewer">Viewer</option>
                        <option value="editor">Editor</option>
                      </select>
                    ) : (
                      <RoleBadge role={member.role} />
                    )}
                    {isOwner && (
                      <button
                        onClick={() => onRemoveMember(member.id)}
                        className="p-1 text-slate hover:text-crimson transition-colors"
                        title="Remove member"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          {isOwner && (
            <div className="px-5 py-3 border-t border-sand/10">
              <button
                onClick={onInvite}
                className="sm:hidden w-full flex items-center justify-center gap-2 py-2 text-sm text-electric hover:text-electric/80"
              >
                <UserPlus className="h-4 w-4" />
                Invite Member
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function TeamsPage() {
  const { user } = useAuth();
  const {
    teams, pendingInvites, loading, error,
    createTeam, renameTeam, deleteTeam,
    inviteMember, removeMember, updateMemberRole,
    acceptInvite, declineInvite,
  } = useTeams();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [inviteTarget, setInviteTarget] = useState<Team | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const handleDeleteTeam = async (team: Team) => {
    if (!confirm(`Delete "${team.name}"? This will remove all members and sermon shares for this team.`)) return;
    try {
      setActionError(null);
      await deleteTeam(team.id);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Failed to delete team');
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    try {
      setActionError(null);
      await removeMember(memberId);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Failed to remove member');
    }
  };

  const handleUpdateRole = async (memberId: string, role: TeamRole) => {
    try {
      setActionError(null);
      await updateMemberRole(memberId, role);
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Failed to update role');
    }
  };

  const ownedTeams = teams.filter(t => t.created_by === user?.id);
  const memberTeams = teams.filter(t => t.created_by !== user?.id);

  if (loading) {
    return (
      <div className="p-4 md:p-8 flex items-center justify-center min-h-48">
        <div className="animate-spin h-6 w-6 border-2 border-electric border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="p-4 pb-24 md:p-8 md:pb-8 max-w-3xl">
      <div className="flex items-center justify-between mb-6 md:mb-8">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-midnight">Teams</h1>
          <p className="text-sm text-slate mt-1">Collaborate on sermons with your staff and volunteers</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-electric text-white rounded-lg hover:bg-electric/90 transition-colors"
        >
          <Plus className="h-4 w-4" />
          <span className="hidden sm:inline">New Team</span>
        </button>
      </div>

      {(error || actionError) && (
        <div className="mb-4 p-3 bg-crimson/10 text-crimson rounded-lg text-sm">
          {error || actionError}
        </div>
      )}

      {/* Pending invites for current user */}
      {pendingInvites.length > 0 && (
        <div className="mb-6">
          <h2 className="text-sm font-semibold text-slate uppercase tracking-wide mb-3">Pending Invites</h2>
          <div className="space-y-2">
            {pendingInvites.map(invite => (
              <div key={invite.id} className="flex items-center justify-between p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div>
                  <span className="font-medium text-midnight text-sm">
                    {(invite as any).teams?.name || 'A team'}
                  </span>
                  <span className="text-sm text-slate ml-2">invited you as <RoleBadge role={invite.role} /></span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => acceptInvite(invite.invite_token)}
                    className="px-3 py-1.5 text-sm bg-emerald text-white rounded-lg hover:bg-emerald/90"
                  >
                    Accept
                  </button>
                  <button
                    onClick={() => declineInvite(invite.invite_token)}
                    className="px-3 py-1.5 text-sm text-slate hover:text-midnight"
                  >
                    Decline
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Teams you own */}
      {ownedTeams.length > 0 && (
        <div className="mb-6">
          <h2 className="text-sm font-semibold text-slate uppercase tracking-wide mb-3">Your Teams</h2>
          <div className="space-y-3 group">
            {ownedTeams.map(team => (
              <TeamCard
                key={team.id}
                team={team}
                isOwner={true}
                onDelete={() => handleDeleteTeam(team)}
                onRename={name => renameTeam(team.id, name)}
                onInvite={() => setInviteTarget(team)}
                onRemoveMember={handleRemoveMember}
                onUpdateRole={handleUpdateRole}
              />
            ))}
          </div>
        </div>
      )}

      {/* Teams you're a member of */}
      {memberTeams.length > 0 && (
        <div className="mb-6">
          <h2 className="text-sm font-semibold text-slate uppercase tracking-wide mb-3">Teams You're In</h2>
          <div className="space-y-3">
            {memberTeams.map(team => (
              <TeamCard
                key={team.id}
                team={team}
                isOwner={false}
                onDelete={() => {}}
                onRename={() => Promise.resolve()}
                onInvite={() => {}}
                onRemoveMember={() => {}}
                onUpdateRole={() => {}}
              />
            ))}
          </div>
        </div>
      )}

      {ownedTeams.length === 0 && memberTeams.length === 0 && pendingInvites.length === 0 && (
        <div className="text-center py-16 text-slate">
          <Users className="h-12 w-12 mx-auto mb-4 opacity-30" />
          <p className="font-medium text-midnight">No teams yet</p>
          <p className="text-sm mt-1">Create a team to start collaborating on sermons</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="mt-4 px-4 py-2 bg-electric text-white rounded-lg hover:bg-electric/90"
          >
            Create Your First Team
          </button>
        </div>
      )}

      {showCreateModal && (
        <CreateTeamModal
          onClose={() => setShowCreateModal(false)}
          onCreate={createTeam}
        />
      )}

      {inviteTarget && (
        <InviteMemberModal
          teamName={inviteTarget.name}
          onClose={() => setInviteTarget(null)}
          onInvite={(email, role, msg) => inviteMember(inviteTarget.id, email, role, msg)}
        />
      )}
    </div>
  );
}
